import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, User, LogOut, ScrollText, BookOpen, FlaskConical, FileText, Newspaper } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import HeaderLogo from './HeaderLogo';

const STUDY_ITEMS = [
  {
    path: '/sushruta-samhita',
    title: 'Sushruta Samhita',
    description: 'The classical surgical text — searchable, translated, cross-linked to modern procedure.',
    icon: ScrollText,
    numeral: '१',
  },
  {
    path: '/textbooks',
    title: 'Textbooks',
    description: 'Modern Shalya Tantra curriculum for BAMS & MS scholars.',
    icon: BookOpen,
    numeral: '२',
  },
  {
    path: '/articles',
    title: 'Articles',
    description: 'In-depth surgical literature, procedure guides, and clinical protocols.',
    icon: FileText,
    numeral: '३',
  },
  {
    path: '/blog',
    title: 'Blog & Insights',
    description: 'Latest updates, expert perspectives, and community discussions.',
    icon: Newspaper,
    numeral: '४',
  },
  {
    path: '/research',
    title: 'Research',
    description: 'Evidence base, trials and publications bridging both sciences.',
    icon: FlaskConical,
    numeral: '५',
  },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [studyOpen, setStudyOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileStudyOpen, setMobileStudyOpen] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const studyTriggerRef = useRef(null);
  const studyCloseTimeoutRef = useRef(null);
  const userButtonRef = useRef(null);

  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setStudyOpen(false);
    setShowUserMenu(false);
  }, [location.pathname]);

  // Prevent background scrolling when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen]);

  const handleStudyMouseEnter = () => {
    if (studyCloseTimeoutRef.current) clearTimeout(studyCloseTimeoutRef.current);
    setStudyOpen(true);
  };

  const handleStudyMouseLeave = () => {
    studyCloseTimeoutRef.current = setTimeout(() => setStudyOpen(false), 120);
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isStudyActive = STUDY_ITEMS.some((item) => isActive(item.path));

  const closeMobileMenu = () => {
    setMobileOpen(false);
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    setMobileOpen(false);
    
    // Revoke refresh token asynchronously on the backend
    apiService.logout().catch((err) => {
      console.error('Logout API error:', err);
    });

    // Clear local authentication credentials
    localStorage.removeItem('vedic_ai_user');
    localStorage.removeItem('vedic_ai_token');
    localStorage.removeItem('vedic_ai_refresh_token');

    // Hard redirect to the public home page
    window.location.href = '/';
  };

  return (
    <header className={`hd ${scrolled ? 'hd--scrolled' : ''}`}>
        <div className="hd-inner">
          <Link to="/" className="hd-brand" aria-label="Vedic Surgery — Home" onClick={closeMobileMenu}>
            <HeaderLogo />
            <span className="hd-wordmark">
              <span className="hd-wordmark-title">
                Vedic <span className="hd-wordmark-accent">Surgery</span>
              </span>
              <span className="hd-tagline">Shalya Tantra · AI</span>
            </span>
          </Link>

          <nav className="hd-nav" aria-label="Main">
            <Link to="/" className={`hd-link ${isActive('/') ? 'hd-link--active' : ''}`}>
              Home
            </Link>
            <Link
              to="/treatment"
              className={`hd-link ${isActive('/treatment') ? 'hd-link--active' : ''}`}
            >
              AYUSH Care
            </Link>

            <div
              className="hd-dropdown"
              onMouseEnter={handleStudyMouseEnter}
              onMouseLeave={handleStudyMouseLeave}
            >
              <button
                type="button"
                ref={studyTriggerRef}
                className={`hd-link hd-dropdown-trigger ${isStudyActive ? 'hd-link--active' : ''}`}
                aria-expanded={studyOpen}
                aria-haspopup="true"
                onClick={() => setStudyOpen((v) => !v)}
              >
                Study
                <ChevronDown size={15} className={`hd-chevron ${studyOpen ? 'hd-chevron--open' : ''}`} />
              </button>

              {studyOpen && (
                <div className="hd-panel" role="menu">
                  <div className="hd-panel-notch" />
                  <ul className="hd-panel-list">
                    {STUDY_ITEMS.map((item) => (
                      <li key={item.path}>
                        <Link to={item.path} className="hd-panel-row" role="menuitem">
                          <span className="hd-panel-icon" aria-hidden="true">
                            <item.icon size={15} />
                          </span>
                          <span className="hd-panel-title-row">
                            <span className="hd-panel-title">{item.title}</span>
                            {item.badge && (
                              <span className="hd-panel-badge">{item.badge}</span>
                            )}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <Link
              to="/jurisprudence"
              className={`hd-link ${isActive('/jurisprudence') ? 'hd-link--active' : ''}`}
            >
              Jurisprudence
            </Link>
            <Link to="/about" className={`hd-link ${isActive('/about') ? 'hd-link--active' : ''}`}>
              About
            </Link>
            <Link to="/team" className={`hd-link ${isActive('/team') ? 'hd-link--active' : ''}`}>
              Team
            </Link>
            <Link to="/contact" className={`hd-link ${isActive('/contact') ? 'hd-link--active' : ''}`}>
              Contact
            </Link>
          </nav>

          <div className="hd-actions">
            {isAuthenticated() ? (
              <div className="hd-user">
                <button
                  ref={userButtonRef}
                  type="button"
                  className="hd-user-button"
                  onClick={() => setShowUserMenu((v) => !v)}
                  aria-expanded={showUserMenu}
                  aria-haspopup="true"
                  aria-label="User menu"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {user?.profileImageUrl ? (
                    <img 
                      src={user.profileImageUrl} 
                      alt={user.name || 'User'} 
                      className="hd-user-avatar" 
                      style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }} 
                    />
                  ) : (
                    <User size={18} />
                  )}
                  <span className="hd-user-name">{user?.name || user?.email || 'User'}</span>
                </button>

                {showUserMenu && (
                  <div className="hd-user-dropdown" role="menu">
                    <Link to="/profile" className="hd-user-dropdown-item" onClick={() => setShowUserMenu(false)} role="menuitem">
                      <User size={16} />
                      <span>Profile</span>
                    </Link>
                    <button type="button" className="hd-user-dropdown-item hd-user-dropdown-item--logout" onClick={handleLogout} role="menuitem">
                      <LogOut size={16} />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="hd-btn hd-btn-ghost">
                  Login
                </Link>
                <Link to="/signup" className="hd-btn hd-btn-primary">
                  <span>Sign Up</span>
                </Link>
              </>
            )}
          </div>

          <button
            type="button"
            className="hd-hamburger"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {mobileOpen &&
          ReactDOM.createPortal(
            <div className="hd-mobile-panel">
              {/* Header Top Bar inside Mobile Panel */}
              <div className="hd-mobile-header-top">
                <Link to="/" className="hd-brand" onClick={closeMobileMenu}>
                  <HeaderLogo />
                  <span className="hd-wordmark">
                    <span className="hd-wordmark-title">
                      Vedic <span className="hd-wordmark-accent">Surgery</span>
                    </span>
                  </span>
                </Link>
                <button
                  type="button"
                  className="hd-mobile-close-btn"
                  onClick={closeMobileMenu}
                  aria-label="Close menu"
                >
                  <X size={24} />
                </button>
              </div>

              <nav className="hd-mobile-nav" aria-label="Mobile">
                <Link to="/" className="hd-mobile-link" onClick={closeMobileMenu}>Home</Link>
                <Link to="/treatment" className="hd-mobile-link" onClick={closeMobileMenu}>AYUSH Care</Link>

                {/* Accordion Toggle for Study Menu */}
                <div 
                  className="hd-mobile-group-header"
                  onClick={() => setMobileStudyOpen((v) => !v)}
                >
                  <span>Study</span>
                  <ChevronDown size={16} className={`hd-chevron ${mobileStudyOpen ? 'hd-chevron--open' : ''}`} />
                </div>
                
                {mobileStudyOpen && (
                  <div className="hd-mobile-study-list">
                    {STUDY_ITEMS.map((item) => (
                      <Link 
                        key={item.path} 
                        to={item.path} 
                        className="hd-mobile-link hd-mobile-link--study"
                        onClick={closeMobileMenu}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <item.icon size={15} color="var(--primary-color, #00d4ff)" />
                          <span>{item.title}</span>
                        </span>
                        <span className="hd-mobile-numeral" aria-hidden="true">{item.numeral}</span>
                      </Link>
                    ))}
                  </div>
                )}

                <Link to="/jurisprudence" className="hd-mobile-link" onClick={closeMobileMenu}>Jurisprudence</Link>
                <Link to="/about" className="hd-mobile-link" onClick={closeMobileMenu}>About</Link>
                <Link to="/team" className="hd-mobile-link" onClick={closeMobileMenu}>Team</Link>
                <Link to="/contact" className="hd-mobile-link" onClick={closeMobileMenu}>Contact</Link>
              </nav>

              {/* Authenticated User Info or Guest Action Buttons */}
              {isAuthenticated() ? (
                <div className="hd-mobile-user-card">
                  <div className="hd-mobile-user-info">
                    {user?.profileImageUrl ? (
                      <img src={user.profileImageUrl} alt={user.name || 'User'} className="hd-mobile-user-avatar" />
                    ) : (
                      <div className="hd-mobile-user-avatar-fallback">
                        <User size={20} />
                      </div>
                    )}
                    <div>
                      <div className="hd-mobile-user-name">{user?.name || user?.email || 'User'}</div>
                      <div className="hd-mobile-user-email">{user?.email}</div>
                    </div>
                  </div>
                  <div className="hd-mobile-user-actions">
                    <Link to="/profile" className="hd-btn hd-btn-ghost" onClick={closeMobileMenu}>
                      <User size={15} /> Profile
                    </Link>
                    <button type="button" className="hd-btn hd-btn-ghost" style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }} onClick={handleLogout}>
                      <LogOut size={15} /> Logout
                    </button>
                  </div>
                </div>
              ) : (
                <div className="hd-mobile-auth">
                  <Link to="/login" className="hd-btn hd-btn-ghost" onClick={closeMobileMenu}>Login</Link>
                  <Link to="/signup" className="hd-btn hd-btn-primary" onClick={closeMobileMenu}><span>Sign Up</span></Link>
                </div>
              )}

            </div>,
            document.body
          )}
    </header>
  );
};

export default Header;
